import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ChartLineUp, ListChecks, Robot, PhoneCall, Gauge, Article, Lightning, PaperPlaneTilt, ChartBar, ChatCircleDots, Toolbox } from "@phosphor-icons/react";
import { CHANNELS, SubTabs } from "@/components/marketing/mk";
import { PauseBar } from "@/components/marketing/PauseBar";
import { MarketingOverview } from "@/components/marketing/Overview";
import { Templates } from "@/components/marketing/Templates";
import { Automation, BulkSend } from "@/components/marketing/Sending";
import { ChannelDashboard, Reports, Inbox } from "@/components/marketing/Reports";
import { EmailTools, WaTools, TrackingSetup } from "@/components/marketing/Tools";
import { AgentTrainer } from "@/components/telecalling/AgentTrainer";
import { CallLists } from "@/components/telecalling/CallLists";
import { CallLog } from "@/components/telecalling/CallLog";

function Telecalling() {
  const [tab, setTab] = useState("lists");
  return (
    <>
      <SubTabs prefix="calls" value={tab} onChange={setTab} tabs={[["lists", "Call lists & FMCSA", ListChecks], ["agent", "Train the AI", Robot], ["log", "Call log", PhoneCall]]} />
      {tab === "lists" && <CallLists />}{tab === "agent" && <AgentTrainer />}{tab === "log" && <CallLog />}
    </>
  );
}

export function Channel({ channel, isAdmin }) {
  const [tab, setTab] = useState("dashboard");
  const tabs = [["dashboard", "Dashboard", Gauge], ["templates", "Templates", Article], ...(isAdmin ? [["auto", "Daily auto-send", Lightning]] : []), ["bulk", "Bulk send", PaperPlaneTilt],
    ["reports", "Reports", ChartBar], ["inbox", "Replies", ChatCircleDots], ...(channel !== "sms" ? [["tools", channel === "whatsapp" ? "Number checker" : "Deliverability", Toolbox]] : [])];
  return (
    <>
      {isAdmin && <PauseBar channel={channel} />}
      <SubTabs prefix={channel} value={tab} onChange={setTab} tabs={tabs} />
      {tab === "dashboard" && <><ChannelDashboard channel={channel} /><Reports channel={channel} /></>}
      {tab === "templates" && <Templates channel={channel} />}
      {tab === "auto" && <Automation channel={channel} />}
      {tab === "bulk" && <BulkSend channel={channel} onSent={() => setTab("reports")} />}
      {tab === "reports" && <Reports channel={channel} />}
      {tab === "inbox" && <Inbox channel={channel} />}
      {tab === "tools" && (channel === "whatsapp" ? <WaTools /> : <EmailTools channel={channel} />)}
    </>
  );
}

export default function Marketing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("overview");
  const items = [["overview", "Command Center", ChartLineUp, "#f59e0b"], ...Object.entries(CHANNELS).filter(([k]) => k !== "trailer").map(([k, c]) => [k, c.label, c.icon, c.color])];
  return (
    <div className="max-w-[1500px] mx-auto">
      <div className="relative overflow-hidden rounded-3xl bg-[#07090f] border border-[#1f2937] px-6 sm:px-8 py-7 mb-6 animate-fade-up" data-testid="mk-hero">
        <div className="absolute inset-0 opacity-[0.35]" style={{ backgroundImage: "radial-gradient(circle at 85% 20%, rgba(245,158,11,.35), transparent 40%), radial-gradient(circle at 10% 90%, rgba(14,165,233,.25), transparent 40%)" }} />
        <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-5">
          <div><p className="text-[11px] font-bold uppercase tracking-[0.28em] text-amber-400">Growth engine</p>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold text-white" data-testid="mk-title">Multichannel Marketing</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">AI calls, SMS, email and WhatsApp to every new carrier FMCSA publishes — automated daily, tracked to the reply.</p></div>
          {isAdmin && <div className="w-full lg:w-[420px] [&_*]:!border-[#1f2937]"><TrackingSetup /></div>}
        </div>
        <div className="relative mt-6 flex flex-wrap gap-2" data-testid="mk-channel-tabs">
          {items.map(([k, l, I, color]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`mk-tab-${k}`}
              className={`h-11 px-4 rounded-2xl text-sm font-semibold inline-flex items-center gap-2 border transition-[background-color,color,transform] duration-200 hover:-translate-y-0.5 ${tab === k ? "bg-white text-[#07090f] border-white" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
              <I size={17} weight={tab === k ? "fill" : "regular"} style={{ color: tab === k ? color : undefined }} />{l}
            </button>
          ))}
        </div>
      </div>
      {tab === "overview" && <MarketingOverview onOpen={setTab} />}
      {tab === "calls" && <Telecalling />}
      {["sms", "email", "whatsapp"].includes(tab) && <Channel key={tab} channel={tab} isAdmin={isAdmin} />}
    </div>
  );
}
